import { array } from 'fp-ts';
import { FormSelectField, FormSelectFieldProps, LocalizedText } from '@mews/framework';
import { DropdownItem, FormMessage, IconName, Stack, Status } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { TooltippedIcon } from '@mews-commander/ui';
import { WithFieldsConfig } from '../types';
import { LockableField } from '../../LockableField';
import { WithEnterpriseIds, WithVoucherIds } from '../../../../../types';
import { useRateIntersection } from '../../../../../queries';

const renderRateKeysItems = (keys: string[]) => keys.map((key) => <DropdownItem key={key} value={key} label={key} />);

type RateKeysFieldProps = Pick<FormSelectFieldProps, 'required'> &
    WithFieldsConfig &
    WithEnterpriseIds &
    WithVoucherIds & {
        onUnlock?: () => void;
    };

export const RateKeysField = ({ fieldsConfig, enterpriseIds, required, voucherIds, onUnlock }: RateKeysFieldProps) => {
    const isAssignedRatesFieldLocked = fieldsConfig?.lockedFields?.includes('rateKeys') || false;
    const isAssignedRatesFieldDisabled = fieldsConfig?.disabledFields?.includes('rateKeys') || false;
    const { data: intersectionData, isFetching } = useRateIntersection({ enterpriseIds, voucherIds });

    const isCreateForm = voucherIds === undefined || array.isEmpty(voucherIds);
    const fields = intersectionData ? renderRateKeysItems(intersectionData.keys) : null;

    const cannotCreateVoucherErrorMessage =
        !isFetching && intersectionData?.keys && array.isEmpty(intersectionData?.keys) ? (
            <FormMessage status={Status.Error}>
                <LocalizedText
                    textKey={
                        enterpriseIds.length > 1
                            ? TextKey.CannotCreateVoucherMultipleProperties
                            : TextKey.CannotCreateVoucherSingleProperty
                    }
                />
            </FormMessage>
        ) : null;

    const noCommonKeysHelperTextKey =
        enterpriseIds.length > 1 ? TextKey.NoCommonRateKeys : TextKey.NoCommonRateKeysSingleProperty;

    return isAssignedRatesFieldDisabled ? (
        <Stack vertical spacing="50">
            <FormSelectField
                multiselect
                name="rateKeys"
                label={<LocalizedText textKey={TextKey.AssignedRateKeys} />}
                disabled
                captionIconWithTooltip={
                    <TooltippedIcon
                        size="small"
                        value={IconName.Info}
                        tooltip={TextKey.VoucherAssignedRateKeysFieldTooltip}
                    />
                }
                helper={!isFetching ? <LocalizedText textKey={noCommonKeysHelperTextKey} /> : null}
                loader={isFetching}
                required={required}
            >
                {fields}
            </FormSelectField>
            {isCreateForm ? cannotCreateVoucherErrorMessage : null}
        </Stack>
    ) : (
        <LockableField
            fieldName="rateKeys"
            locked={isAssignedRatesFieldLocked}
            lockedReasonText={TextKey.VouchersValuesAreDifferent}
            tooltipTextKey={TextKey.UnlockableVouchersField}
            onUnlock={onUnlock}
        >
            {(isLocked) => (
                <FormSelectField
                    multiselect
                    name="rateKeys"
                    label={<LocalizedText textKey={TextKey.AssignedRateKeys} />}
                    disabled={isLocked || isFetching}
                    loader={isFetching}
                    required={required}
                    captionIconWithTooltip={
                        <TooltippedIcon
                            size="small"
                            value={IconName.Info}
                            tooltip={TextKey.VoucherAssignedRateKeysFieldTooltip}
                        />
                    }
                >
                    {fields}
                </FormSelectField>
            )}
        </LockableField>
    );
};
